import React, { useState, useEffect } from 'react';
import XMLCodeEditor from '../Components/XMLParser/XMLCodeEditor';
import { AddRulesModal } from '../Components/XMLParser/AddRulesModal';
import { CollectionCard } from '../Components/XMLParser/CollectionCard';
import { RuleCard } from '../Components/XMLParser/RuleCard';
import { detectAvailableCollections, mergeXmlDocuments } from '../Components/XMLParser/xmlMerger';
import {
    IconFileCode,
    IconPuzzle,
    IconTerminal,
    IconPackage,
    IconApps,
    IconFileText,
    IconPlus,
} from '@tabler/icons-react';

const getTypeStyle = (type) => {
    const typeLower = type.toLowerCase();
    switch (typeLower) {
        case 'exe':
            return {
                icon: <IconFileCode className="w-5 h-5 text-indigo-500" />,
                badgeClass: 'bg-indigo-100 text-indigo-800',
            };
        case 'dll':
            return {
                icon: <IconPuzzle className="w-5 h-5 text-indigo-500" />,
                badgeClass: 'bg-indigo-100 text-indigo-800',
            };
        case 'script':
            return {
                icon: <IconTerminal className="w-5 h-5 text-green-500" />,
                badgeClass: 'bg-green-100 text-green-800',
            };
        case 'msi':
            return {
                icon: <IconPackage className="w-5 h-5 text-yellow-500" />,
                badgeClass: 'bg-yellow-100 text-yellow-800',
            };
        case 'appx':
            return {
                icon: <IconApps className="w-5 h-5 text-blue-500" />,
                badgeClass: 'bg-blue-100 text-blue-800',
            };
        default:
            return {
                icon: <IconFileText className="w-5 h-5 text-gray-500" />,
                badgeClass: 'bg-gray-100 text-gray-800',
            };
    }
};

export const AppLockerXMLParser = () => {
    // Core State
    const [xmlInput, setXmlInput] = useState('');
    const [parsedOutput, setParsedOutput] = useState([]);
    const [errors, setErrors] = useState([]);
    const [openCollections, setOpenCollections] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResultCount, setSearchResultCount] = useState(0);

    // Add Rules Modal State
    const [showAddRulesModal, setShowAddRulesModal] = useState(false);
    const [additionalXml, setAdditionalXml] = useState('');
    const [addRulesErrors, setAddRulesErrors] = useState([]);
    const [availableCollections, setAvailableCollections] = useState([]);
    const [selectedCollection, setSelectedCollection] = useState('');
    const [duplicateWarning, setDuplicateWarning] = useState(null);
    const [pendingMergeData, setPendingMergeData] = useState(null);

    // Modal Management Functions
    const openAddRulesModal = () => {
        const collections = detectAvailableCollections(xmlInput);
        setAvailableCollections(collections);

        if (collections.length === 1) {
            setSelectedCollection(collections[0].type);
        } else {
            const exeCollection = collections.find(c => c.type === 'Exe');
            setSelectedCollection(exeCollection ? 'Exe' : (collections[0]?.type || ''));
        }

        setShowAddRulesModal(true);
    };

    const closeAddRulesModal = () => {
        setShowAddRulesModal(false);
        setAdditionalXml('');
        setAddRulesErrors([]);
        setSelectedCollection('');
        setDuplicateWarning(null);
        setPendingMergeData(null);
    };

    // Duplicate Detection Functions
    const getBinaryNameFromRule = (rule) => {
        const conditions = rule.getElementsByTagName('FilePublisherCondition');
        if (conditions.length > 0) {
            return conditions[0].getAttribute('BinaryName');
        }
        return null;
    };

    const getPublisherNameFromRule = (rule) => {
        const conditions = rule.getElementsByTagName('FilePublisherCondition');
        if (conditions.length > 0) {
            return conditions[0].getAttribute('PublisherName');
        }
        return null;
    };

    const getPathFromRule = (rule) => {
        const conditions = rule.getElementsByTagName('FilePathCondition');
        if (conditions.length > 0) {
            return conditions[0].getAttribute('Path');
        }
        return null;
    };

    const getHashFromRule = (rule) => {
        const hashConditions = rule.getElementsByTagName('FileHashCondition');
        if (hashConditions.length > 0) {
            const fileHashes = hashConditions[0].getElementsByTagName('FileHash');
            if (fileHashes.length > 0) {
                return fileHashes[0].getAttribute('Data');
            }
        }
        return null;
    };

    const serializeConditions = (rule) => {
        const conditions = rule.getElementsByTagName('Conditions')[0];
        if (!conditions) return '';

        const serializer = new XMLSerializer();
        return serializer.serializeToString(conditions).toLowerCase().replace(/\s+/g, ' ').trim();
    };

    const serializeExceptions = (rule) => {
        const exceptions = rule.getElementsByTagName('Exceptions')[0];
        if (!exceptions) return '';

        const serializer = new XMLSerializer();
        return serializer.serializeToString(exceptions).toLowerCase().replace(/\s+/g, ' ').trim();
    };

    const getConditionsDetails = (rule) => {
        const conditions = rule.getElementsByTagName('Conditions')[0];
        if (!conditions) return [];

        const details = [];

        // FilePublisherCondition
        const publisherConditions = conditions.getElementsByTagName('FilePublisherCondition');
        Array.from(publisherConditions).forEach(cond => {
            const publisher = cond.getAttribute('PublisherName') || '';
            const product = cond.getAttribute('ProductName') || '';
            const binary = cond.getAttribute('BinaryName') || '';

            details.push({
                type: 'Publisher',
                details: `Publisher: ${publisher}, Product: ${product}, Binary: ${binary}`
            });
        });

        // FilePathCondition
        const pathConditions = conditions.getElementsByTagName('FilePathCondition');
        Array.from(pathConditions).forEach(cond => {
            const path = cond.getAttribute('Path') || '';
            details.push({
                type: 'Path',
                details: `Path: ${path}`
            });
        });

        // FileHashCondition
        const hashConditions = conditions.getElementsByTagName('FileHashCondition');
        Array.from(hashConditions).forEach(cond => {
            const fileHashes = cond.getElementsByTagName('FileHash');
            Array.from(fileHashes).forEach(hash => {
                const hashData = hash.getAttribute('Data') || '';
                const hashType = hash.getAttribute('Type') || 'SHA256';
                details.push({
                    type: 'Hash',
                    details: `${hashType}: ${hashData.substring(0, 16)}...`
                });
            });
        });

        return details;
    };

    const getExceptionsDetails = (rule) => {
        const exceptions = rule.getElementsByTagName('Exceptions')[0];
        if (!exceptions) return [];

        const details = [];

        // FilePublisherCondition exceptions
        const publisherExceptions = exceptions.getElementsByTagName('FilePublisherCondition');
        Array.from(publisherExceptions).forEach(exc => {
            const publisher = exc.getAttribute('PublisherName') || '';
            const product = exc.getAttribute('ProductName') || '';
            const binary = exc.getAttribute('BinaryName') || '';

            details.push({
                type: 'Publisher Exception',
                details: `Publisher: ${publisher}, Product: ${product}, Binary: ${binary}`
            });
        });

        // FilePathCondition exceptions
        const pathExceptions = exceptions.getElementsByTagName('FilePathCondition');
        Array.from(pathExceptions).forEach(exc => {
            const path = exc.getAttribute('Path') || '';
            details.push({
                type: 'Path Exception',
                details: `Path: ${path}`
            });
        });

        return details;
    };

    const checkForDuplicates = (originalXml, additionalXml, targetCollectionType) => {
        const parser = new DOMParser();
        const originalDoc = parser.parseFromString(originalXml, 'text/xml');

        // Prepare additional XML
        let cleanedXml = additionalXml.trim();
        cleanedXml = cleanedXml.replace(/<\/?RuleCollection[^>]*>/g, '');
        if (!cleanedXml.startsWith('<?xml')) {
            cleanedXml = `<?xml version="1.0"?><temp>${cleanedXml}</temp>`;
        }
        const additionalDoc = parser.parseFromString(cleanedXml, 'text/xml');

        // Get new rules
        const newRules = [];
        const ruleTypes = ['FilePublisherRule', 'FilePathRule', 'FileHashRule'];
        ruleTypes.forEach(ruleType => {
            const rules = additionalDoc.getElementsByTagName(ruleType);
            Array.from(rules).forEach(rule => newRules.push(rule));
        });

        // Find existing rules in target collection
        const existingRules = [];
        const collections = originalDoc.getElementsByTagName('RuleCollection');
        Array.from(collections).forEach(collection => {
            if (collection.getAttribute('Type') === targetCollectionType) {
                ruleTypes.forEach(ruleType => {
                    const rules = collection.getElementsByTagName(ruleType);
                    Array.from(rules).forEach(rule => existingRules.push(rule));
                });
            }
        });

        const duplicates = [];

        newRules.forEach(newRule => {
            const newId = newRule.getAttribute('Id');
            const newName = newRule.getAttribute('Name') || '';
            const newBinaryName = getBinaryNameFromRule(newRule);
            const newPublisherName = getPublisherNameFromRule(newRule);
            const newPath = getPathFromRule(newRule);
            const newHash = getHashFromRule(newRule);
            const newConditions = serializeConditions(newRule);
            const newExceptions = serializeExceptions(newRule);
            const newConditionsDetails = getConditionsDetails(newRule);
            const newExceptionsDetails = getExceptionsDetails(newRule);

            existingRules.forEach(existingRule => {
                const existingId = existingRule.getAttribute('Id');
                const existingName = existingRule.getAttribute('Name') || '';
                const existingBinaryName = getBinaryNameFromRule(existingRule);
                const existingPublisherName = getPublisherNameFromRule(existingRule);
                const existingPath = getPathFromRule(existingRule);
                const existingHash = getHashFromRule(existingRule);
                const existingConditions = serializeConditions(existingRule);
                const existingExceptions = serializeExceptions(existingRule);
                const existingConditionsDetails = getConditionsDetails(existingRule);
                const existingExceptionsDetails = getExceptionsDetails(existingRule);

                let duplicateType = null;
                let details = '';
                let matchingConditions = [];
                let matchingExceptions = [];

                // Check for identical ID
                if (newId && existingId && newId === existingId) {
                    duplicateType = 'Identical ID';
                    details = `ID: ${newId}`;
                    matchingConditions = newConditionsDetails;
                    matchingExceptions = newExceptionsDetails;
                }
                // Check for identical conditions + exceptions (complete rule match)
                else if (newConditions && existingConditions &&
                    newConditions === existingConditions &&
                    newExceptions === existingExceptions) {
                    duplicateType = 'Identical Conditions & Exceptions';
                    details = 'Complete rule logic match detected';
                    matchingConditions = newConditionsDetails;
                    matchingExceptions = newExceptionsDetails;
                }
                // Check for identical conditions only
                else if (newConditions && existingConditions && newConditions === existingConditions) {
                    duplicateType = 'Identical Conditions';
                    details = 'Same rule conditions detected';
                    matchingConditions = newConditionsDetails;
                }
                // Check for identical name
                else if (newName && existingName && newName.toLowerCase() === existingName.toLowerCase()) {
                    duplicateType = 'Identical Name';
                    details = `Name: "${newName}"`;
                    matchingConditions = newConditionsDetails;
                }
                // Check for identical hash
                else if (newHash && existingHash && newHash.toLowerCase() === existingHash.toLowerCase()) {
                    duplicateType = 'Identical File Hash';
                    details = `Hash: ${newHash.substring(0, 16)}...`;
                    matchingConditions = newConditionsDetails.filter(c => c.type === 'Hash');
                }
                // Check for identical binary + publisher
                else if (newBinaryName && existingBinaryName &&
                    newBinaryName.toLowerCase() === existingBinaryName.toLowerCase() &&
                    newPublisherName && existingPublisherName &&
                    newPublisherName.toLowerCase() === existingPublisherName.toLowerCase()) {
                    duplicateType = 'Identical Binary + Publisher';
                    details = `Binary: ${newBinaryName}, Publisher: ${newPublisherName}`;
                    matchingConditions = newConditionsDetails.filter(c => c.type === 'Publisher');
                }
                // Check for identical path
                else if (newPath && existingPath && newPath.toLowerCase() === existingPath.toLowerCase()) {
                    duplicateType = 'Identical Path';
                    details = `Path: ${newPath}`;
                    matchingConditions = newConditionsDetails.filter(c => c.type === 'Path');
                }

                if (duplicateType) {
                    duplicates.push({
                        type: duplicateType,
                        details: details,
                        newRuleName: newName || 'Unnamed Rule',
                        existingRuleName: existingName || 'Unnamed Rule',
                        matchingConditions: matchingConditions,
                        matchingExceptions: matchingExceptions
                    });
                }
            });
        });

        return duplicates;
    };

    // Rule Addition Functions
    const handleAddRules = () => {
        if (!additionalXml.trim()) {
            setAddRulesErrors(['Please enter additional XML rules']);
            return;
        }

        if (!selectedCollection) {
            setAddRulesErrors(['Please select a target collection']);
            return;
        }

        try {
            // Check for duplicates first
            const duplicates = checkForDuplicates(xmlInput, additionalXml, selectedCollection);

            if (duplicates.length > 0) {
                setDuplicateWarning(duplicates);
                setPendingMergeData({ xmlInput, additionalXml, selectedCollection });
                return;
            }

            // No duplicates found, proceed with merge
            const mergedXml = mergeXmlDocuments(xmlInput, additionalXml, selectedCollection);
            setAdditionalXml('');
            setAddRulesErrors([]);
            setShowAddRulesModal(false);
            handleAnalyze(mergedXml);
        } catch (error) {
            setAddRulesErrors([error.message]);
        }
    };

    const handleConfirmDuplicateAdd = () => {
        if (pendingMergeData) {
            try {
                const mergedXml = mergeXmlDocuments(
                    pendingMergeData.xmlInput,
                    pendingMergeData.additionalXml,
                    pendingMergeData.selectedCollection
                );
                setAdditionalXml('');
                setAddRulesErrors([]);
                setShowAddRulesModal(false);
                setDuplicateWarning(null);
                setPendingMergeData(null);
                handleAnalyze(mergedXml);
            } catch (error) {
                setAddRulesErrors([error.message]);
                setDuplicateWarning(null);
                setPendingMergeData(null);
            }
        }
    };

    const handleCancelDuplicateAdd = () => {
        setDuplicateWarning(null);
        setPendingMergeData(null);
    };

    // UI Interaction Functions
    const toggleCollection = (idx) => {
        setOpenCollections((prev) => {
            const updated = [...prev];
            updated[idx] = !updated[idx];
            return updated;
        });
    };

    // Rule Processing Functions
    const processRulesForCollection = (collection, searchQuery) => {
        const rules = [];
        ['FilePublisherRule', 'FilePathRule', 'FileHashRule'].forEach((tag) => {
            rules.push(...Array.from(collection.getElementsByTagName(tag)));
        });

        return rules.map((rule, ruleIdx) => {
            const name = rule.getAttribute('Name') || '';
            const id = rule.getAttribute('Id') || '';
            const description = rule.getAttribute('Description') || 'No description provided.';
            const sid = rule.getAttribute('UserOrGroupSid') || '';

            const conditions = rule.getElementsByTagName('Conditions')[0];
            const exceptions = rule.getElementsByTagName('Exceptions')[0];

            // Enhanced text extraction for search
            let conditionText = '';
            let allAttributeText = '';

            // Extract all text content from conditions
            if (conditions) {
                const walker = document.createTreeWalker(conditions, NodeFilter.SHOW_TEXT, null);
                while (walker.nextNode()) {
                    conditionText += walker.currentNode.nodeValue + ' ';
                }

                // Extract all attribute values from condition elements
                const extractAttributes = (element) => {
                    if (element.attributes) {
                        Array.from(element.attributes).forEach(attr => {
                            allAttributeText += attr.value + ' ';
                        });
                    }
                    Array.from(element.children).forEach(child => {
                        extractAttributes(child);
                    });
                };
                extractAttributes(conditions);
            }

            // Extract all text content and attributes from exceptions
            if (exceptions) {
                const walker = document.createTreeWalker(exceptions, NodeFilter.SHOW_TEXT, null);
                while (walker.nextNode()) {
                    conditionText += walker.currentNode.nodeValue + ' ';
                }

                const extractAttributes = (element) => {
                    if (element.attributes) {
                        Array.from(element.attributes).forEach(attr => {
                            allAttributeText += attr.value + ' ';
                        });
                    }
                    Array.from(element.children).forEach(child => {
                        extractAttributes(child);
                    });
                };
                extractAttributes(exceptions);
            }

            // Combine all searchable text (case insensitive)
            const combinedText = `${name} ${id} ${description} ${sid} ${conditionText} ${allAttributeText}`.toLowerCase();

            if (searchQuery && !combinedText.includes(searchQuery.toLowerCase())) {
                return null;
            }

            return <RuleCard key={ruleIdx} rule={rule} ruleIdx={ruleIdx} searchQuery={searchQuery} />;
        }).filter(Boolean);
    };

    // Main Analysis Function
    const handleAnalyze = (content) => {
        setXmlInput(content);
        try {
            const parser = new DOMParser();
            const dom = parser.parseFromString(content, 'text/xml');
            const parseErrors = dom.getElementsByTagName('parsererror');

            if (parseErrors.length > 0) {
                const errorMsg = parseErrors[0].textContent;
                setErrors([`⚠️ Error: Invalid XML structure.\n${errorMsg}`]);
                setParsedOutput([]);
                setSearchResultCount(0);
                return;
            }

            const ruleCollections = dom.getElementsByTagName('RuleCollection');
            if (ruleCollections.length === 0) {
                setErrors(['⚠️ No valid RuleCollection found in the XML.']);
                setParsedOutput([]);
                setSearchResultCount(0);
                return;
            }

            let resultCount = 0;

            const result = Array.from(ruleCollections).map((collection, idx) => {
                const type = collection.getAttribute('Type');
                const enforcementMode = collection.getAttribute('EnforcementMode') || 'Not specified';
                const { icon, badgeClass } = getTypeStyle(type);

                const filteredContent = processRulesForCollection(collection, searchQuery);
                resultCount += filteredContent.length;

                if (filteredContent.length === 0) return null;

                return {
                    idx,
                    type,
                    enforcementMode,
                    icon,
                    badgeClass,
                    content: filteredContent,
                };
            }).filter(Boolean);

            setParsedOutput(result);
            setOpenCollections(Array(result.length).fill(false));
            setErrors([]);
            setSearchResultCount(resultCount);
        } catch (err) {
            setErrors([`⚠️ Unexpected error: ${err.message}`]);
            setParsedOutput([]);
            setSearchResultCount(0);
        }
    };

    // Effect Hook
    useEffect(() => {
        if (xmlInput) {
            handleAnalyze(xmlInput);
        }
    }, [searchQuery]);

    // Render
    return (
        <>
            <div className="page-header d-print-none">
                <div className="container-xl">
                    <div className="row g-2 align-items-center">
                        <div className="col">
                            <h2 className="page-title">AppLocker XML Parser</h2>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body">
                <div className="container-xl">
                    <div className="card">
                        <div className="card-header">
                            <h3 className="card-title">Paste and analyze AppLocker XML configurations...</h3>
                            {xmlInput && (
                                <div className="card-actions">
                                    <button
                                        className="btn btn-primary"
                                        onClick={openAddRulesModal}
                                    >
                                        <IconPlus className="w-4 h-4 me-1" />
                                        Add Rules
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className="card-body">
                            <XMLCodeEditor
                                value={xmlInput}
                                onChange={handleAnalyze}
                                className="w-full p-2 border border-gray-400 rounded mb-4 font-mono"
                            />

                            {errors.length > 0 && (
                                <div className="mt-4 text-red-600">
                                    {errors.map((err, idx) => (
                                        <p key={idx} className="mb-2 whitespace-pre-wrap">🚨 {err}</p>
                                    ))}
                                </div>
                            )}

                            {xmlInput && (
                                <div className="mb-4">
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder="Search rules and conditions..."
                                        className="w-full p-2 border border-gray-400 rounded"
                                    />
                                    <p className="text-sm text-gray-600 mt-2">
                                        {searchQuery ? (
                                            <>🔍 {searchResultCount} matching rule{searchResultCount !== 1 ? 's' : ''} found.</>
                                        ) : (
                                            <>Type to search rules and conditions.</>
                                        )}
                                    </p>
                                </div>
                            )}

                            {parsedOutput.length > 0 ? (
                                parsedOutput.map((collection, idx) => (
                                    <CollectionCard
                                        key={idx}
                                        collection={collection}
                                        idx={idx}
                                        isOpen={openCollections[idx]}
                                        onToggle={toggleCollection}
                                    />
                                ))
                            ) : (
                                xmlInput && searchQuery && (
                                    <p className="text-gray-500 italic mt-4">
                                        No matching rules found for "<strong>{searchQuery}</strong>".
                                    </p>
                                )
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <AddRulesModal
                showModal={showAddRulesModal}
                additionalXml={additionalXml}
                setAdditionalXml={setAdditionalXml}
                addRulesErrors={addRulesErrors}
                availableCollections={availableCollections}
                selectedCollection={selectedCollection}
                setSelectedCollection={setSelectedCollection}
                onClose={closeAddRulesModal}
                onAddRules={handleAddRules}
                duplicateWarning={duplicateWarning}
                onConfirmDuplicateAdd={handleConfirmDuplicateAdd}
                onCancelDuplicateAdd={handleCancelDuplicateAdd}
            />
        </>
    );
};